import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import FadeIn from '../FadeIn';
import LoadingIndicator from '../LoadingIndicator';
import SignedContract from '../contract/SignedContract';
import ContractPayment from '../contract/ContractPayment';
import useApiClient from '../../hooks/api/useApiClient';
import useFocus from '../../hooks/utility/useFocus';
import useQueryParams from '../../hooks/utility/useQueryParams';

const QueryParams = {
  Flow: 'flow',
  Status: 'status',
};

const ContractPaymentFlow = {
  Name: 'contract_payment',
  Status: {
    Success: 'success',
    Cancel: 'cancel',
  },
};

export default function ContractPaymentStep({ moveForward }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:ContractPaymentStep' });
  const { ServiceContractsManager } = useApiClient();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const query = useQueryParams();

  useEffect(() => {
    loadContract().then((data) => data && !data.is_immediate_payment_required && moveForward());
  }, []);

  // Returning from the Stripe payment flow — re-check the contract and advance once paid.
  useFocus(
    () => loadContract(false).then((data) => data && !data.is_immediate_payment_required && moveForward()),
    null,
    true,
  );

  useEffect(() => {
    query.get(QueryParams.Flow) === ContractPaymentFlow.Name &&
      query.get(QueryParams.Status) === ContractPaymentFlow.Status.Success &&
      toast.promise(
        new Promise((resolve, reject) => {
          setTimeout(
            () => loadContract().then((_data) => (_data?.is_immediate_payment_required ? reject() : resolve())),
            5000,
          );
        }),
        {
          pending: t('toasts.payment.pending'),
          success: t('toasts.payment.success'),
          error: t('toasts.payment.error'),
        },
      );
  }, []);

  const loadContract = async (withLoading = true) => {
    withLoading && setLoading(true);
    const { success, data } = await ServiceContractsManager.get();
    withLoading && setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_load_data'));

      return null;
    }

    setContract(data);

    return data;
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <FadeIn>
      <SignedContract data={contract} />
      {contract?.is_immediate_payment_required && (
        <>
          <hr className="my-lg w-full" />
          <ContractPayment data={contract} />
        </>
      )}
    </FadeIn>
  );
}
